import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { GroupProductApi } from 'api';
import type {
  GroupProduct,
  MapCreateGroupProductRequest,
} from 'api/methods/GroupProductApi';

/**
 * [GroupProduct] Query Key 생성
 * --
 */
export const groupProductQueryKeys = createQueryKeys('groupproduct', {
  getGroupProduct: (
    idx: number,
    params?: { [key: string]: string | number }
  ) => [idx, params],
  getGroupProducts: (params?: { [key: string]: string | number }) => [params],
  getMapGroupProducts: (params?: { [key: string]: string | number }) => [
    params,
  ],
});

/**
 * [GroupProduct] 그룹 상품 전체 조회
 * --
 */
export const useGetGroupProducts = (
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: groupProductQueryKeys.getGroupProducts(params).queryKey,
    queryFn: () => GroupProductApi.getGroupProducts(params),
    ...options,
  });

  return data;
};

/**
 * [GroupProduct] 그룹 상품 전체 조회(맵핑,정책 쪼인)
 * --
 */
export const useGetMapGroupProducts = (
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: groupProductQueryKeys.getMapGroupProducts(params).queryKey,
    queryFn: () => GroupProductApi.getMapGroupProducts(params),
    ...options,
  });

  return data;
};

/**
 * [GroupProduct] 그룹 상품 상세 조회
 * --
 */
export const useGetGroupProduct = (
  idx: number,
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: groupProductQueryKeys.getGroupProduct(idx, params).queryKey,
    queryFn: () => GroupProductApi.getGroupProduct(idx, params),
    ...options,
  });

  return data;
};

/**
 * [GroupProduct] 그룹 상품 생성
 * --
 */
export const useCreateGroupProduct = (body: GroupProduct) => {
  const { mutateAsync: createGroupProduct, ...result } = useMutation({
    mutationFn: (variables: GroupProduct) =>
      GroupProductApi.createGroupProduct(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { createGroupProduct, ...result };
};

/**
 * [GroupProduct] 그룹 상품 수정
 * --
 */
export const useUpdateGroupProduct = (body: GroupProduct) => {
  const { mutateAsync: updateGroupProduct, ...result } = useMutation({
    mutationFn: (variables: GroupProduct) =>
      GroupProductApi.updateGroupProduct(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { updateGroupProduct, ...result };
};

/**
 * [GroupProduct] 그룹 상품 삭제
 * --
 */
export const useDeleteGroupProduct = (idx: number) => {
  const { mutateAsync: deleteGroupProduct, ...result } = useMutation({
    mutationFn: (variables: number) =>
      GroupProductApi.deleteGroupProduct(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { deleteGroupProduct, ...result };
};

/**
 * [GroupProduct] 그룹 상품 생성
 * --
 */
export const useUpsertGroupProduct = () => {
  const { mutateAsync: upsertGroupProduct, ...result } = useMutation({
    mutationFn: (variables: GroupProduct[]) =>
      GroupProductApi.upsertGroupProduct(variables),
  });

  return { upsertGroupProduct, ...result };
};

/**
 * [GroupProduct] 그룹 상품 생성(맵핑, 정책)
 * --
 */
export const useCreateMapGroupProduct = (
  body: MapCreateGroupProductRequest
) => {
  const { mutateAsync: createMapGroupProduct, ...result } = useMutation({
    mutationFn: (variables: MapCreateGroupProductRequest) =>
      GroupProductApi.mapCreateGroupProduct(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { createMapGroupProduct, ...result };
};
